/**
 * Audit log search + export wire shapes (Delivery roadmap Tier 2.1).
 *
 * Drives the new `GET /api/orgs/:orgId/audit/search` endpoint, the
 * cursor-paginated frontend panel, and the CSV export. Designed so
 * compliance teams can answer questions like:
 *
 *   - "Who changed the SSO config last quarter?"
 *   - "Show me every PATCH to the admin config in the last 7 days."
 *   - "Export every cross-org action by platform_admin staff."
 *
 * All filters are optional; when none are provided the endpoint
 * behaves like a paged GET of the most-recent rows. The text query
 * is a Postgres FTS expression — see the operator runbook.
 */
import { z } from 'zod';

import { AiProvenanceSchema } from './audit-provenance';

/** Coarse surface tags surfaced in the panel filter dropdown. */
export const AuditSurfaceSchema = z.enum([
  'access', // login, MFA, sessions, password reset
  'org', // members, roles, invites, ownership transfer
  'sso', // SSO + SCIM provisioning
  'flows', // flow CRUD, branches, reviews
  'runs', // run lifecycle, retries, cancels
  'config', // admin_config edits
  'deploy', // deployment + verify + rollback
  'integration', // github / jira / slack
  'data', // gdpr / exports / deletions
  'security', // dast / api-key rotation / leaks
  'other', // catch-all
]);
export type AuditSurface = z.infer<typeof AuditSurfaceSchema>;

/** Filter envelope. All fields are optional. */
export const AuditSearchRequestSchema = z.object({
  /** ISO 8601 lower bound (inclusive). */
  from: z.string().datetime().optional(),
  /** ISO 8601 upper bound (exclusive). */
  to: z.string().datetime().optional(),
  /** Filter by user IDs (actors). Empty array = no constraint. */
  userIds: z.array(z.string().uuid()).max(50).optional(),
  /** Filter by surface tags. Empty array = no constraint. */
  surfaces: z.array(AuditSurfaceSchema).max(20).optional(),
  /** Filter by exact action codes (e.g. SCIM_USER_CREATED). */
  actions: z.array(z.string().min(1).max(64)).max(50).optional(),
  /** Free-text FTS query against action + entity_name + meta::text. */
  q: z.string().max(500).optional(),
  /** Opaque pagination cursor; null/undefined = first page. */
  cursor: z.string().nullable().optional(),
  /** Page size, capped at 200. */
  limit: z.number().int().min(1).max(200).default(50),
  /**
   * Show only rows that carry an `aiProvenance` payload (i.e. rows
   * produced by an AI call). Drives the "AI only" toggle on the
   * audit-log panel. Server-side this uses the partial index from
   * migration 150 so the filter is cheap.
   */
  onlyAi: z.boolean().optional(),
});
export type AuditSearchRequest = z.infer<typeof AuditSearchRequestSchema>;

export const AuditSearchRowSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  userId: z.string().uuid().nullable(),
  actorEmail: z.string().nullable(),
  actorName: z.string().nullable(),
  actorRole: z.string().nullable(),
  action: z.string(),
  surface: AuditSurfaceSchema,
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  entityName: z.string().nullable(),
  targetOrgId: z.string().uuid().nullable(),
  bypassReasons: z.array(z.string()).nullable(),
  meta: z.unknown().nullable(),
  count: z.number().int().min(1),
  firstAt: z.string(),
  lastAt: z.string(),
  /**
   * Present when the row was produced by an AI call. The wire shape
   * is defined in `audit-provenance.ts`. The audit-log panel renders
   * an "AI" pill for any row where this is non-null.
   */
  aiProvenance: AiProvenanceSchema.nullable().optional(),
});
export type AuditSearchRow = z.infer<typeof AuditSearchRowSchema>;

export const AuditSearchResponseSchema = z.object({
  rows: z.array(AuditSearchRowSchema),
  nextCursor: z.string().nullable(),
  /** Approximate total — bounded by a server-side cap so big windows
   *  don't lock the UI waiting for COUNT(*). */
  approxTotal: z.number().int().min(0).nullable(),
  /** Range covered by this page; useful for the empty state. */
  rangeFromUtc: z.string().nullable(),
  rangeToUtc: z.string().nullable(),
});
export type AuditSearchResponse = z.infer<typeof AuditSearchResponseSchema>;

/** GET /api/orgs/:orgId/audit/:id — single-row diff payload. */
export const AuditDiffResponseSchema = z.object({
  row: AuditSearchRowSchema,
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
});
export type AuditDiffResponse = z.infer<typeof AuditDiffResponseSchema>;
